import {
  Box,
  Button,
  DialogBackdrop,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogPositioner,
  DialogRoot,
  DialogTitle,
  HStack,
  Portal,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react"
import { zodResolver } from "@hookform/resolvers/zod"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { memo, useState } from "react"
import { useForm } from "react-hook-form"
import { FaRocket } from "react-icons/fa"
import { z } from "zod"

import { Field } from "@/components/atoms/Field"
import { useCustomToast } from "@/hooks/useCustomToast"
import { usePermissions } from "@/hooks/usePermissions"
import { api } from "@/services/api"
import type { ClassTemplateListResponse } from "@/types/class_template"
import type { SessionListResponse } from "@/types/session"
import { unwrapStandardResponse } from "@/utils/apiResponse"

// Form validation schema
const generateSchema = z.object({
  session_id: z.string().min(1, "Please select a session"),
  class_template_id: z.string().min(1, "Please select a class template"),
})

type GenerateFormData = z.infer<typeof generateSchema>

interface GenerateClassSessionsProps {
  onSuccess?: () => void
  onCancel?: () => void
}

const GenerateClassSessions = memo(
  ({ onSuccess, onCancel }: GenerateClassSessionsProps = {}) => {
    const [isOpen, setIsOpen] = useState(false)
    const { hasPermission } = usePermissions()
    const queryClient = useQueryClient()
    const { showSuccessToast, showErrorToast } = useCustomToast()

    // Check permission
    if (!hasPermission("class_sessions:generate")) {
      return null
    }

    // Fetch sessions for dropdown
    const { data: sessionsData, isLoading: isLoadingSessions } = useQuery({
      queryKey: ["sessions", { limit: 100 }],
      queryFn: async () => {
        const response = await api.sessions.readSessionsApiV1SessionsGet({
          skip: 0,
          limit: 100,
          sortBy: "created_at",
          sortOrder: "desc",
        })
        const payload = unwrapStandardResponse<SessionListResponse>(response)
        return payload?.items ?? []
      },
      enabled: isOpen,
    })

    // Fetch class templates for dropdown
    const { data: classTemplatesData, isLoading: isLoadingTemplates } =
      useQuery({
        queryKey: ["classTemplates", { limit: 100 }],
        queryFn: async () => {
          const response =
            await api.classTemplates.readClassTemplatesApiV1ClassTemplatesGet({
              skip: 0,
              limit: 100,
              sortBy: "created_at",
              sortOrder: "desc",
            })
          const payload =
            unwrapStandardResponse<ClassTemplateListResponse>(response)
          return payload?.items ?? []
        },
        enabled: isOpen,
      })

    // Form setup
    const {
      register,
      handleSubmit,
      formState: { errors },
      reset,
    } = useForm<GenerateFormData>({
      resolver: zodResolver(generateSchema),
      defaultValues: {
        session_id: "",
        class_template_id: "",
      },
    })

    // Generate mutation
    const generateMutation = useMutation({
      mutationFn: async (data: GenerateFormData) => {
        const response =
          await api.classTemplates.generateClassTemplateMeetingsApiV1ClassTemplatesClassTemplateIdGenerateMeetingsPost(
            {
              classTemplateId: data.class_template_id,
              sessionId: data.session_id,
            },
          )
        return unwrapStandardResponse<Record<string, unknown>>(response)
      },
      onSuccess: (data) => {
        const meetingsCreated =
          (data?.meetings_created as number | undefined) ?? 0
        const totalOccurrences =
          (data?.session_occurrences as number | undefined) ?? 0

        showSuccessToast(
          `Created ${meetingsCreated} new class sessions. Session now has ${totalOccurrences} total occurrences.`,
        )

        // Invalidate queries to refresh data
        queryClient.invalidateQueries({ queryKey: ["classSessions"] })

        handleClose()
        onSuccess?.()
      },
      onError: (error: Error) => {
        showErrorToast(error.message || "Failed to generate class sessions")
      },
    })

    const handleClose = () => {
      setIsOpen(false)
      reset()
      onCancel?.()
    }

    const onSubmit = async (data: GenerateFormData) => {
      await generateMutation.mutateAsync(data)
    }

    // The queryFn above already extracts .items, so data is always an array
    const sessions = sessionsData ?? []
    const classTemplates = classTemplatesData ?? []
    const isLoading = isLoadingSessions || isLoadingTemplates

    return (
      <>
        <Button
          onClick={() => setIsOpen(true)}
          colorPalette="green"
          size="sm"
          aria-label="Generate class sessions"
        >
          <FaRocket fontSize="14px" aria-hidden="true" />
          Generate Sessions
        </Button>

        <DialogRoot
          open={isOpen}
          onOpenChange={(e) =>
            !generateMutation.isPending &&
            (e.open ? setIsOpen(true) : handleClose())
          }
          size="md"
        >
          <Portal>
            <DialogBackdrop />
            <DialogPositioner>
              <DialogContent maxH="90vh" display="flex" flexDirection="column">
                <DialogHeader>
                  <DialogTitle>Generate Class Sessions</DialogTitle>
                </DialogHeader>
                <DialogCloseTrigger disabled={generateMutation.isPending} />

                <DialogBody overflowY="auto" flex="1">
                  <form id="generate-form" onSubmit={handleSubmit(onSubmit)}>
                    <VStack gap={4} alignItems="stretch">
                      <Box
                        p={3}
                        bg="blue.50"
                        borderRadius="md"
                        borderWidth="1px"
                        borderColor="blue.200"
                      >
                        <Text fontSize="sm" color="blue.800">
                          Select a session and class template to automatically
                          generate all class sessions based on the template's
                          schedule (day, time) and the session's date range.
                        </Text>
                      </Box>

                      {isLoading ? (
                        <HStack justifyContent="center" py={4}>
                          <Spinner size="sm" />
                          <Text fontSize="sm" color="gray.600">
                            Loading options...
                          </Text>
                        </HStack>
                      ) : (
                        <>
                          <Field
                            label="Session"
                            invalid={!!errors.session_id}
                            errorText={errors.session_id?.message}
                            required
                          >
                            <Box
                              as="select"
                              {...register("session_id")}
                              width="100%"
                              px={3}
                              py={2}
                              borderRadius="md"
                              borderWidth="1px"
                              borderColor={
                                errors.session_id ? "red.500" : "gray.200"
                              }
                              fontSize="sm"
                              _focus={{
                                borderColor: "blue.500",
                                outline: "none",
                              }}
                            >
                              <option value="">Select a session...</option>
                              {sessions.map((session) => (
                                <option key={session.id} value={session.id}>
                                  {session.name} ({session.start_week} -{" "}
                                  {session.end_week})
                                </option>
                              ))}
                            </Box>
                          </Field>

                          <Field
                            label="Class Template"
                            invalid={!!errors.class_template_id}
                            errorText={errors.class_template_id?.message}
                            required
                          >
                            <Box
                              as="select"
                              {...register("class_template_id")}
                              width="100%"
                              px={3}
                              py={2}
                              borderRadius="md"
                              borderWidth="1px"
                              borderColor={
                                errors.class_template_id
                                  ? "red.500"
                                  : "gray.200"
                              }
                              fontSize="sm"
                              _focus={{
                                borderColor: "blue.500",
                                outline: "none",
                              }}
                            >
                              <option value="">
                                Select a class template...
                              </option>
                              {classTemplates.map((template) => (
                                <option key={template.id} value={template.id}>
                                  {template.name} - {template.day_of_week}{" "}
                                  {template.start_time}
                                </option>
                              ))}
                            </Box>
                          </Field>
                        </>
                      )}
                    </VStack>
                  </form>
                </DialogBody>

                <DialogFooter>
                  <HStack gap={2} justifyContent="flex-end">
                    <Button
                      variant="outline"
                      onClick={handleClose}
                      disabled={generateMutation.isPending}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      form="generate-form"
                      colorPalette="green"
                      loading={generateMutation.isPending}
                      loadingText="Generating..."
                      disabled={isLoading}
                    >
                      Generate Sessions
                    </Button>
                  </HStack>
                </DialogFooter>
              </DialogContent>
            </DialogPositioner>
          </Portal>
        </DialogRoot>
      </>
    )
  },
)

GenerateClassSessions.displayName = "GenerateClassSessions"

export default GenerateClassSessions
export type { GenerateClassSessionsProps }
